import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import WhatsAppCard from './WhatsAppCard';
import './HeroCarousel.css';

const HeroCarousel = () => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchBanners();
    }, []);

    useEffect(() => {
        if (banners.length === 0) return;

        // Auto-advance carousel every 5 seconds
        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) =>
                prevIndex === banners.length - 1 ? 0 : prevIndex + 1
            );
        }, 5000);

        return () => clearInterval(interval);
    }, [banners.length]);

    const fetchBanners = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/banners/active`);
            let data = response.data;
            
            // Injetar o banner do WhatsApp na posição 2 (índice 1)
            const whatsappBanner = {
                id: 'whatsapp-special-hero',
                type: 'whatsapp',
                title: 'Receba Ofertas no WhatsApp',
                linkUrl: `${process.env.REACT_APP_API_URL}/join-whatsapp`
            };
            
            const updatedBanners = [...data];
            if (updatedBanners.length >= 1) {
                updatedBanners.splice(1, 0, whatsappBanner);
            } else {
                updatedBanners.push(whatsappBanner);
            }
            
            setBanners(updatedBanners);
            setError(null);
        } catch (error) {
            console.error('Erro ao buscar banners:', error);
            setError('Erro ao carregar banners');
        } finally {
            setLoading(false);
        }
    };

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    const goToPrevious = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? banners.length - 1 : prevIndex - 1
        );
    };

    const goToNext = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === banners.length - 1 ? 0 : prevIndex + 1
        );
    };

    if (loading) {
        return (
            <div className="hero-carousel loading">
                <div className="loading-spinner">Carregando...</div>
            </div>
        );
    }

    if (error || banners.length === 0) {
        return null;
    }

    const currentBanner = banners[currentIndex];
    const isWhatsApp = currentBanner.type === 'whatsapp';
    const hasText = !isWhatsApp && (currentBanner.title || currentBanner.subtitle);
    const hasLink = !isWhatsApp && currentBanner.linkUrl;
    const shouldRenderOverlay = hasText || hasLink;
    const isInitialBanner = currentBanner.id === 'banner-intimo-1';

    return (
        <div className="hero-carousel">
            <div className="carousel-container">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentIndex}
                        className="carousel-slide"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        {isWhatsApp ? (
                            <WhatsAppCard isHero={true} />
                        ) : isInitialBanner ? (
                            /* Wrapper centralizado para a logo compacta */
                            <div className="initial-banner-wrapper">
                                <motion.img
                                    src={currentBanner.imageUrl}
                                    alt={currentBanner.title || 'Banner'}
                                    className="carousel-image initial-logo"
                                    initial={{ scale: 1.1 }}
                                    animate={{ scale: 1 }}
                                    transition={{ duration: 6, ease: "easeOut" }}
                                />
                            </div>
                        ) : (
                            /* Imagem cheia padrão com Zoom Out */
                            <motion.img
                                src={currentBanner.imageUrl}
                                alt={currentBanner.title || 'Banner'}
                                className="carousel-image"
                                initial={{ scale: 1.1 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 6, ease: "easeOut" }}
                            />
                        )}

                        {/* Overlay de Texto/Ação (Condicional) */}
                        {shouldRenderOverlay && (
                            <div className={`carousel-overlay ${isInitialBanner ? 'centered' : ''}`}>
                                <motion.div
                                    className={`carousel-content ${isInitialBanner ? 'centered' : ''}`}
                                    initial={{ y: 100, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{
                                        type: "spring",
                                        stiffness: 100,
                                        damping: 15,
                                        delay: 0.3
                                    }}
                                >
                                    {currentBanner.title && (
                                        <h1 className="carousel-title">{currentBanner.title}</h1>
                                    )}
                                    {currentBanner.subtitle && (
                                        <p className="carousel-subtitle">{currentBanner.subtitle}</p>
                                    )}
                                    {currentBanner.linkUrl && (
                                        currentBanner.linkUrl.startsWith('/') || currentBanner.linkUrl.startsWith('#') ? (
                                            <a
                                                href={currentBanner.linkUrl}
                                                className="carousel-cta"
                                            >
                                                {isInitialBanner ? 'Ver todas as ofertas' : 'Ver Ofertas'}
                                            </a>
                                        ) : (
                                            <a
                                                href={currentBanner.linkUrl}
                                                className="carousel-cta"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {isInitialBanner ? 'Ver todas as ofertas' : 'Ver Ofertas'}
                                            </a>
                                        )
                                    )}
                                </motion.div>
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>

                {/* Navigation Arrows */}
                {banners.length > 1 && (
                    <>
                        <button
                            className="carousel-arrow carousel-arrow-left"
                            onClick={goToPrevious}
                            aria-label="Banner anterior"
                        >
                            <i className="fas fa-chevron-left"></i>
                        </button>
                        <button
                            className="carousel-arrow carousel-arrow-right"
                            onClick={goToNext}
                            aria-label="Próximo banner"
                        >
                            <i className="fas fa-chevron-right"></i>
                        </button>
                    </>
                )}

                {/* Dots Indicator */}
                {banners.length > 1 && (
                    <div className="carousel-dots">
                        {banners.map((_, index) => (
                            <button
                                key={index}
                                className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                                onClick={() => goToSlide(index)}
                                aria-label={`Ir para banner ${index + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default HeroCarousel;
